import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

interface GenerateEmptyCouponsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (generateQty: number) => Promise<void>;
  programName?: string;
}

const GenerateEmptyCouponsDialog: React.FC<GenerateEmptyCouponsDialogProps> = ({
  isOpen,
  onClose,
  onGenerate,
  programName = 'this program'
}) => {
  const [generateQty, setGenerateQty] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (generateQty < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onGenerate(generateQty);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate empty coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setGenerateQty(1);
    setError(null);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Generate Empty Coupons</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              Generate empty coupons for <span className="font-medium">{programName}</span>. 
              These coupons can be assigned to companies later.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity to Generate *
              </label>
              <input
                type="number"
                value={generateQty}
                onChange={(e) => setGenerateQty(parseInt(e.target.value) || 1)}
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the number of empty coupons to generate (1-1000)
              </p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Generating...</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Coupons
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateEmptyCouponsDialog;
